import classNames from 'classnames';
import * as React from 'react';
import { CustomTooltipInfo, Heading, Surface } from '@quality24/design-system';
import { v4 as uuid } from 'uuid';

import {
  Customizations,
  GraphQLFilter,
  HasPermissionFn,
  LayoutViewData,
} from '../../@types';
import { FilterViewContainer } from '../FilterViewContainer';
import { DataViewContainer } from '../DataViewContainer';
import { TextViewContainer } from '../TextViewContainer';

import styles from './LayoutViewContainer.module.scss';
import CustomViewContainer from '../CustomViewContainer';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: LayoutViewData;
  customizations?: Customizations;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
  hasPermission?: HasPermissionFn;
}

/**
 * <LayoutViewContainer> page
 */
export const LayoutViewContainer: React.FunctionComponent<Props> = ({
  className,
  style,
  data: {
    className: configClassName,
    style: configStyle,
    title,
    tooltipContent,
    options,
    items,
  },
  baseFilter,
  permissionFilter,
  customizations,
  hasPermission,
  ...htmlElementProps
}) => (
  <div className={className} style={style} {...htmlElementProps}>
    <Surface
      className={classNames(styles.surface, {
        'on-surface': options?.renderOnSurface === true,
      })}
      padding="sm"
    >
      <div className="d-flex flex-items-center gap-2 mb-3">
        {title && (
          <Heading as="h2" size="sm2" weight="bold">
            {title}
          </Heading>
        )}
        {tooltipContent && <CustomTooltipInfo info={tooltipContent} />}
      </div>

      <div className={configClassName} style={configStyle}>
        {items.map((d) => {
          if (hasPermission && d.permission && !hasPermission(d.permission)) {
            return null;
          }

          // Page filters
          if (d.type === 'filter-view') {
            return (
              <FilterViewContainer
                key={uuid()}
                data={d}
                baseFilter={baseFilter}
                permissionFilter={permissionFilter}
                hasPermission={hasPermission}
              />
            );
          }

          // Data views
          if (d.type === 'data-view') {
            return (
              <DataViewContainer
                key={uuid()}
                data={d}
                customizations={customizations}
                baseFilter={baseFilter}
                permissionFilter={permissionFilter}
                hasPermission={hasPermission}
              />
            );
          }

          // Text views
          if (d.type === 'text-view') {
            return <TextViewContainer key={uuid()} data={d} />;
          }

          // Layout views
          if (d.type === 'layout-view') {
            return (
              <LayoutViewContainer
                key={uuid()}
                data={d}
                customizations={customizations}
                baseFilter={baseFilter}
                permissionFilter={permissionFilter}
                hasPermission={hasPermission}
              />
            );
          }

          // Custom views
          if (d.type === 'custom-view') {
            return (
              <CustomViewContainer
                key={uuid()}
                data={d}
                customizations={customizations}
                baseFilter={baseFilter}
                permissionFilter={permissionFilter}
              />
            );
          }

          return null;
        })}
      </div>
    </Surface>
  </div>
);

export default LayoutViewContainer;
